import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Textarea, MediaPlayer } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { useFocusOnLoad } from '../../../../utils/animations'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import { RESPONSE_STATES } from 'utils/constants'

const MAX_CHAR_LENGTH = 140

const completeTextEntryQuestion = (props, text) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = text
    ? [
        {
          label,
          value: text,
          question: id,
        },
      ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: text ? RESPONSE_STATES.answered : RESPONSE_STATES.skipped,
    answer,
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

function TextEntry(props) {
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    isSibling = false,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
  } = props
  const [text, setText] = useState('')
  const { attributes, label, imageSrc, mediaSrc } = question
  const { required } = attributes
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    const storedValue = storedAnswer?.[0]?.value
    if (storedValue) {
      onInput(storedValue)
      // Complete immediately if not suppressing auto-advance
      if (!props.suppressAutoAdvance) {
        completeTextEntryQuestion(props, storedValue)
      }
    }
  }, [])

  const onInput = (value) => {
    setAllRequiredAnswered(false, id)
    setText(value)
    // Only persist to Redux — do not call checkLogicAndComplete on every keystroke
    // (sibling mode hides the footer; running full complete would resolve "next"
    // question and crash if nextQuestionId is missing from webSurvey.questions).
    try {
      const { question, webSurvey, completeQuestion } = props
      const { label, type, attributes } = question
      const { required } = attributes
      const answer = value
        ? [
            {
              label,
              value,
              question: id,
            },
          ]
        : []
      const submission = {
        id,
        label,
        name: id,
        type,
        required,
        state: value ? 'ANSWERED' : 'SKIPPED',
        answer,
      }
      if (typeof completeQuestion === 'function' && webSurvey) {
        completeQuestion(submission, webSurvey.order?.[0])
      }
    } catch (e) {
      console.warn('TextEntry autosave failed', { error: e })
    }
  }

  return (
    <div ref={props.forwardedRef}>
      <SurveyQuestion id={id}>
        <MakeScrollable>
          <div className={`survey-question text-entry${_className}`}>
            <p className='question-label'>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatAndSanitizeHTML(label),
                }}
              />
              <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
            </p>
            <Textarea
              maxLength={MAX_CHAR_LENGTH}
              onChange={(value) => onInput(value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (allowEnterPress(required, text)) {
                    completeTextEntryQuestion(props, text)
                  }
                }
              }}
              value={text}
              placeholder={strings.type_in_answer_placeholder}
            />
            <div className='character-count-row responsive'>
              <span className='character-count'>
                <b>{`${text.length}/${MAX_CHAR_LENGTH}`}</b>
              </span>
            </div>
            <div className='character-count-row'>
              {!isSibling && (
                <QuestionFooter
                  completed={!!text}
                  id={id}
                  isConsent={isConsent}
                  isLastQuestion={isLastQuestion}
                  loading={submissionLoading}
                  onComplete={() => completeTextEntryQuestion(props, text)}
                  question={question}
                  renderBackButton={renderBackButton}
                  strings={strings}
                  submissionSurvey={submissionSurvey}
                  webSurvey={webSurvey}
                />
              )}
              <span className='character-count'>
                <b>{`${text.length}/${MAX_CHAR_LENGTH}`}</b>
              </span>
            </div>
          </div>
        </MakeScrollable>
      </SurveyQuestion>
    </div>
  )
}

const ForwardedTextEntry = React.forwardRef((props, ref) => (
  <TextEntry {...props} forwardedRef={ref} />
))

export default ForwardedTextEntry

TextEntry.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  isSibling: PropTypes.bool,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  imageSrc: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
